import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Chat } from '@/types';
import ChatCard from '@/components/chat/ChatCard';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSupabaseStore } from '@/store/SupabaseStore';

interface ChatGridProps {
  chats: Chat[];
  loadingMessages?: boolean;
  onOpenChat?: (id: string, title?: string) => void;
}

const ChatGrid = ({ chats, loadingMessages, onOpenChat }: ChatGridProps) => {
  const isMobile = useIsMobile();
  const { fetchChatMessages } = useSupabaseStore();
  const [loadedChats, setLoadedChats] = useState<string[]>([]);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const chatRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const initialBatchLoaded = useRef(false);

  const loadChatBatch = useCallback(async (chatsToLoad: Chat[]) => {
    setLoadingBatch(true);
    await Promise.all(
      chatsToLoad.map(async (chat) => {
        if (loadedChats.includes(chat.id)) return;
        if (chat.messages && chat.messages.length > 0) {
          setLoadedChats((prev) => [...prev, chat.id]);
          return;
        }
        try {
          await fetchChatMessages(chat.id);
          setLoadedChats((prev) => [...prev, chat.id]);
        } catch (error) {
          console.error(`Error loading messages for chat ${chat.id}:`, error);
        }
      })
    );
    setLoadingBatch(false);
  }, [fetchChatMessages, loadedChats]);

  useEffect(() => {
    if (!chats.length) return;
    if (initialBatchLoaded.current) return;
    const initialBatch = isMobile ? 3 : 5;
    const chatsToLoad = chats.slice(0, initialBatch).filter((chat) => !loadedChats.includes(chat.id));
    if (chatsToLoad.length === 0) {
      initialBatchLoaded.current = true;
      return;
    }
    loadChatBatch(chatsToLoad).then(() => {
      initialBatchLoaded.current = true;
    });
  }, [chats, isMobile, loadChatBatch]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visibleChats = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target.id)
          .filter((id) => !loadedChats.includes(id));
        if (visibleChats.length === 0 || loadingBatch) return;
        const chatsToLoad = chats.filter((chat) => visibleChats.includes(chat.id));
        loadChatBatch(chatsToLoad);
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );
    chatRefs.current.forEach((ref) => {
      if (ref && observerRef.current) {
        observerRef.current.observe(ref);
      }
    });
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [chats, loadedChats, loadingBatch, loadChatBatch]);

  if (!chats.length) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {chats.map((chat) => {
        const isLoaded = loadedChats.includes(chat.id) || (chat.messages && chat.messages.length > 0);
        return (
          <div
            key={chat.id}
            id={chat.id}
            ref={(ref) => {
              if (ref) {
                chatRefs.current.set(chat.id, ref);
              } else {
                chatRefs.current.delete(chat.id);
              }
            }}
          >
            <ChatCard
              id={chat.id}
              title={chat.title}
              updatedAt={chat.updatedAt}
              viewCount={chat.viewCount || 0}
              tokensUsed={chat.tokensUsed || 0}
              isFavorite={chat.isFavorite || false}
              messages={chat.messages || []}
              loadingMessages={!isLoaded && loadingMessages}
              messageCount={chat.messageCount || 0}
              project_id={chat.project_id}
              onOpenChat={onOpenChat}
            />
          </div>
        );
      })}
      {isMobile && chats.length > 6 && (
        <div className="col-span-1 rounded-lg bg-muted/20 p-4 text-center">
          <p className="text-sm text-muted-foreground">
            Showing 6 of {chats.length} chats for better performance.
            Scroll to see more.
          </p>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatGrid);
